<template>
  <!-- Loading -->
  <div class="details-loading" v-show="loader">
    <q-spinner-dots color="primary" size="40px" />
  </div>

  <div class="details-page" v-show="imovel.title">
    <!-- ── Gallery ───────────────────────────────────── -->
    <div class="gallery-section">
      <div class="inner">
        <button class="back-btn" @click="voltarLista">
          <q-icon name="arrow_back" size="16px" />
          <span>Voltar</span>
        </button>

        <div class="row q-col-gutter-md">
          <!-- Main image -->
          <div class="col-12 col-md-8">
            <div class="main-img-wrap cursor-pointer" @click="abrirGaleria(0)">
              <q-img :src="imagemPrincipal" :ratio="16 / 10" class="main-img">
                <template v-slot:loading>
                  <q-spinner-gears color="white" size="40px" />
                </template>
              </q-img>

              <!-- Badges -->
              <div class="img-badges">
                <span
                  class="img-badge"
                  :class="
                    imovel.objective === 'Venda' ? 'badge-green' : 'badge-blue'
                  "
                >
                  {{ imovel.objective }}
                </span>
                <span class="img-badge badge-dark">{{ imovel.condition }}</span>
              </div>

              <!-- Nav arrows -->
              <button class="nav-btn nav-left" @click.stop="imagemAnterior">
                <q-icon name="chevron_left" size="20px" />
              </button>
              <button class="nav-btn nav-right" @click.stop="proximaImagem">
                <q-icon name="chevron_right" size="20px" />
              </button>

              <!-- Photo counter -->
              <div class="photo-count">
                <q-icon name="photo_library" size="13px" />
                {{ imagemAtual + 1 }} / {{ imovel.imagens.length }}
              </div>
            </div>
          </div>

          <!-- Thumbnails -->
          <div class="col-12 col-md-4">
            <div class="thumb-grid">
              <div
                v-for="(img, index) in imovel.imagens.slice(0, 4)"
                :key="index"
                class="thumb-item cursor-pointer"
                :class="{ 'thumb-active': imagemAtual === index }"
                @click="selecionarImagem(index)"
              >
                <q-img :src="img" :ratio="1" />
                <div
                  v-if="index === 3 && imovel.imagens.length > 4"
                  class="thumb-more"
                >
                  +{{ imovel.imagens.length - 4 }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Info ───────────────────────────────────────── -->
    <div class="info-section">
      <div class="inner">
        <div class="row q-col-gutter-xl">
          <!-- Left: details -->
          <div class="col-12 col-md-8">
            <!-- Title block -->
            <div class="title-block">
              <div class="prop-type-label">{{ imovel.property_type }}</div>
              <h1 class="prop-title">{{ imovel.title }}</h1>
              <div class="location-row">
                <q-icon name="location_on" size="15px" />
                <span>{{ imovel.location }}</span>
              </div>
              <div class="price-block">
                <div class="price-label">
                  {{
                    imovel.objective === "Venda"
                      ? "Preço de venda"
                      : "Arrendamento / mês"
                  }}
                </div>
                <div class="price-value">
                  {{ formatPrice(imovel.price, imovel.currency) }}
                </div>
              </div>
            </div>

            <div class="section-div" />

            <!-- Features -->
            <div class="info-block">
              <div class="block-title">Características</div>
              <div class="row q-col-gutter-md">
                <div class="col-6 col-sm-3">
                  <div class="feat-card">
                    <div class="feat-icon">
                      <q-icon name="bed" size="20px" />
                    </div>
                    <div class="feat-value">{{ imovel.bedroom_amount }}</div>
                    <div class="feat-label">Quartos</div>
                  </div>
                </div>
                <div class="col-6 col-sm-3">
                  <div class="feat-card">
                    <div class="feat-icon">
                      <q-icon name="bathtub" size="20px" />
                    </div>
                    <div class="feat-value">{{ imovel.bathroom_amount }}</div>
                    <div class="feat-label">Banheiros</div>
                  </div>
                </div>
                <div class="col-6 col-sm-3">
                  <div class="feat-card">
                    <div class="feat-icon">
                      <q-icon name="square_foot" size="20px" />
                    </div>
                    <div class="feat-value">{{ imovel.area }}</div>
                    <div class="feat-label">m²</div>
                  </div>
                </div>
                <div class="col-6 col-sm-3">
                  <div class="feat-card">
                    <div class="feat-icon">
                      <q-icon name="directions_car" size="20px" />
                    </div>
                    <div class="feat-value">{{ imovel.garage_space }}</div>
                    <div class="feat-label">Vagas</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="section-div" />

            <!-- Description -->
            <div class="info-block">
              <div class="block-title">Descrição</div>
              <!-- <p class="desc-text">{{ imovel.description }}</p>
                -->
              <q-card-section
                v-html="imovel.description"
                style="margin-left: -16px"
              />
            </div>
          </div>

          <!-- Right: contact card -->
          <div class="col-12 col-md-4">
            <div class="contact-card">
              <!-- Agent -->
              <div class="agent-row">
                <div class="agent-avatar">
                  <img
                    v-if="imovel.user.photo"
                    :src="imovel.user.photo"
                    alt="Agente"
                  />
                  <img
                    v-else
                    src="https://amzn-s3-goldenkey.s3.us-east-2.amazonaws.com/properties/icon2.png"
                    alt="Agente"
                  />
                </div>
                <div>
                  <div class="agent-name">{{ imovel.user.name }}</div>
                  <div class="agent-role">Agente Imobiliário</div>
                </div>
              </div>

              <div class="card-div" />

              <!-- Contact buttons -->
              <div class="contact-actions">
                <button
                  v-if="imovel.user.whatsapp"
                  class="cta-btn btn-wa"
                  @click="contatoWhatsApp"
                >
                  <q-icon name="whatsapp" size="17px" />
                  <span>WhatsApp</span>
                </button>
                <button
                  v-if="imovel.user.phone"
                  class="cta-btn btn-call"
                  @click="ligarAgora"
                >
                  <q-icon name="phone" size="17px" />
                  <span>Ligar Agora</span>
                </button>
              </div>

              <div class="card-div" />

              <!-- Meta -->
              <div class="meta-row">
                <span class="meta-key">Referência</span>
                <span class="meta-val">{{ imovel.slug }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-key">Tipo</span>
                <span class="meta-val">{{ imovel.property_type }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-key">Objectivo</span>
                <span class="meta-val">{{ imovel.objective }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-key">Condição</span>
                <span class="meta-val">{{ imovel.condition }}</span>
              </div>

              <template v-if="canDelete">
                <div class="card-div" />

                <button
                  class="cta-btn btn-edit"
                  title="Editar imóvel"
                  @click.stop="editarImovel(imovel)"
                >
                  <q-icon name="edit" size="16px" />
                  <span>Editar Imóvel</span>
                </button>

                <div style="padding-top: 10px"></div>

                <button
                  class="cta-btn btn-delete"
                  :disabled="deleting"
                  @click="deleteImovel"
                >
                  <q-spinner-dots v-if="deleting" size="17px" />
                  <template v-else>
                    <q-icon name="delete_outline" size="17px" />
                    <span>Eliminar Imóvel</span>
                  </template>
                </button>

                <div v-if="canManageStatus" class="status-block">
  <div class="card-div" />

  <div v-if="managedPropertyLoaded">
    <div class="meta-row">
      <span class="meta-key">Estado</span>
      <span class="status-badge" :class="`status-badge--${imovel.status}`">
        {{ propStatusLabel(imovel.status) }}
      </span>
    </div>

    <div v-if="imovel.rejection_reason" class="reject-note">
      {{ imovel.rejection_reason }}
    </div>
  </div>
  <div v-else class="text-caption text-grey q-mb-sm">
    A carregar o estado do imóvel...
  </div>

  <q-banner
    v-if="activationError"
    dense
    class="bg-red-1 text-negative q-mb-sm"
  >
    {{ activationError }}
  </q-banner>

  <button
    v-if="managedPropertyLoaded && !['active', 'pending_review'].includes(imovel.status)"
    class="action-btn action-btn--activate"
    :disabled="!canActivate || actionLoading === imovel.slug"
    @click="handleActivate"
  >
    <q-icon name="publish" size="16px" class="q-mr-xs" />
    <span v-if="actionLoading === imovel.slug">...</span>
    <span v-else>Enviar para validação</span>
    <q-tooltip v-if="!canActivate">
      Quota esgotada — faça upgrade do seu plano
    </q-tooltip>
  </button>

  <button
    v-if="managedPropertyLoaded && imovel.status === 'active'"
    class="action-btn action-btn--deactivate"
    :disabled="actionLoading === imovel.slug"
    @click="confirmDeactivate"
  >
    <q-icon name="toggle_off" size="16px" class="q-mr-xs" />
    <span v-if="actionLoading === imovel.slug">...</span>
    <span v-else>Desactivar</span>
  </button>

  <button
    v-if="managedPropertyLoaded && Number(store.state.auth.user?.role) === 1 && imovel.status === 'pending_review'"
    class="action-btn action-btn--activate"
    :disabled="actionLoading === imovel.slug"
    @click="approveProperty"
  >
    <q-icon name="verified" size="16px" class="q-mr-xs" />
    <span v-if="actionLoading === imovel.slug">...</span>
    <span v-else>Aprovar e activar</span>
  </button>

  <div v-if="managedPropertyLoaded && imovel.status === 'pending_review'" class="pending-note">
    {{ Number(store.state.auth.user?.role) === 1 ? "Este imóvel aguarda a sua validação." : "Aguarda validação de um administrador." }}
  </div>
</div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Fullscreen gallery dialog -->
    <q-dialog v-model="galeriaAberta" maximized>
      <q-card class="bg-black">
        <q-bar class="bg-transparent">
          <q-space />
          <q-btn flat dense icon="close" color="white" v-close-popup />
        </q-bar>
        <q-card-section
          class="flex flex-center"
          style="height: calc(100vh - 50px)"
        >
          <q-carousel
            v-model="imagemAtual"
            animated
            navigation
            infinite
            arrows
            control-color="primary"
            class="gallery-carousel"
          >
            <q-carousel-slide
              v-for="(img, index) in imovel.imagens"
              :key="index"
              :name="index"
              :img-src="img"
            />
          </q-carousel>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import store from "@/vuex/store";
import Swal from "sweetalert2";
import apiClient from "@/config/api";

const router = useRouter();
const imagemAtual = ref(0);
const galeriaAberta = ref(false);
const deleting = ref(false);
const actionLoading = ref(null);
const activationError = ref(null);
const managedPropertyLoaded = ref(false);

const canDelete = computed(() => {
  const user = store.state.auth.user;
  if (!user) return false;
  return Number(user.role) === 1 || Number(user.id) === Number(imovel.value.user?.id);
});

const canManageStatus = computed(() => {
  const user = store.state.auth.user;
  return Boolean(
    user &&
      (Number(user.role) === 1 ||
        Number(user.id) === Number(imovel.value.user?.id)),
  );
});

const subscription = computed(() => store.state.subscriptions.mySubscription);
const canActivate = computed(() => {
  if (Number(store.state.auth.user?.role) === 1) return true;
  if (!subscription.value || subscription.value.status !== "active") return false;
  return (subscription.value.available_slots ?? 0) > 0;
});

const imovel = ref({
  id: null,
  status: "",
  rejection_reason: "",
  title: "",
  slug: "",
  property_type: "",
  location: "",
  price: 0,
  currency: "",
  area: 0,
  bedroom_amount: 0,
  bathroom_amount: 0,
  garage_space: 0,
  condition: "",
  objective: "",
  description: "",
  imagens: [],
  user: {
    name: "",
    phone: "",
    email: "",
    photo: "",
    whatsapp: "",
  },
});

watch(
  [canManageStatus, () => imovel.value.slug],
  async ([canManage, slug]) => {
    managedPropertyLoaded.value = false;
    activationError.value = null;
    imovel.value.id = null;
    imovel.value.status = "";
    imovel.value.rejection_reason = "";
    if (!canManage || !slug) return;

    try {
      await getManagedProperty();
    } catch (error) {
      console.error("Error loading managed property:", error);
      activationError.value =
        error.message || "Não foi possível carregar o estado do imóvel.";
    }
  },
  { flush: "post" },
);

const propStatusLabel = (s) => {
  const map = {
    active: "Activo",
    inactive: "Inactivo",
    draft: "Rascunho",
    expired: "Expirado",
    pending_review: "Em revisão",
  };
  return map[s] || s;
};

let loader = ref(true);

const contato = ref({
  nome: "",
  email: "",
  telefone: "",
  mensagem: `Olá! Tenho interesse no imóvel - ${imovel.value.title}.`,
});

const imagemPrincipal = computed(() => imovel.value.imagens[imagemAtual.value]);

const formatPrice = (price) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "MZN",
    minimumFractionDigits: 0,
  }).format(price);
};
getImovel();
function getImovel() {
  console.log("ok");
  window.scrollTo(0, 0);
  store
    .dispatch("imovel/getImovelBySlug", router.currentRoute.value.params.slug)
    .then((res) => {
      imovel.value = res;
      loader.value = false;
      contato.value.mensagem = `Olá! Tenho interesse no imóvel - ${imovel.value.title}.`;
      if (canManageStatus.value) {
        store.dispatch("subscriptions/getMySubscription").catch((error) => {
          console.error("Error loading subscription:", error);
          activationError.value = "Não foi possível carregar os dados da subscrição.";
        });
      }
    });
}
const voltarLista = () => {
  router.back();
};

const selecionarImagem = (index) => {
  imagemAtual.value = index;
};

const imagemAnterior = () => {
  if (imagemAtual.value > 0) {
    imagemAtual.value--;
  } else {
    imagemAtual.value = imovel.value.imagens.length - 1;
  }
};

const proximaImagem = () => {
  if (imagemAtual.value < imovel.value.imagens.length - 1) {
    imagemAtual.value++;
  } else {
    imagemAtual.value = 0;
  }
};

const abrirGaleria = (index) => {
  imagemAtual.value = index;
  galeriaAberta.value = true;
};

// const toggleFavorito = () => {
//   console.log('Toggle favorito');
// };

// const enviarMensagem = () => {
//   console.log('Enviar mensagem:', contato.value);
// };

const contatoWhatsApp = () => {
  const mensagem = encodeURIComponent(contato.value.mensagem);
  window.open(
    `https://wa.me/${imovel.value.user.whatsapp}?text=${mensagem}`,
    "_blank",
  );
};

const ligarAgora = () => {
  window.location.href = `tel:${imovel.value.user.phone}`;
};

const deleteImovel = async () => {
  const result = await Swal.fire({
    title: "Eliminar imóvel?",
    text: "Esta acção é permanente e não pode ser revertida.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc2626",
    cancelButtonColor: "#64748b",
    confirmButtonText: "Sim, eliminar",
    cancelButtonText: "Cancelar",
  });

  if (!result.isConfirmed) return;

  deleting.value = true;
  try {
    await store.dispatch("imovel/destroy", imovel.value.slug);
    await Swal.fire({
      icon: "success",
      title: "Imóvel eliminado.",
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 2000,
    });
    router.push("/imoveis");
  } catch {
    Swal.fire({
      icon: "error",
      title: "Erro ao eliminar o imóvel.",
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 2500,
    });
  } finally {
    deleting.value = false;
  }
};

const editarImovel = (imovel) => {
  const slug = imovel.slug;

  if (!slug) {
    Swal.fire({
      icon: "error",
      title: "Slug do imóvel indisponível",
      text: "Não foi possível abrir a edição porque a API não enviou o slug deste imóvel.",
      confirmButtonText: "Fechar",
    });
    return;
  }

  router.push({ name: "edit-imovel-steped", params: { slug } });
};

const getManagedProperty = async () => {
  const property = await store.dispatch(
    "subscriptions/getMyPropertyBySlug",
    imovel.value.slug,
  );
  if (!property) {
    throw new Error("Não foi possível encontrar este imóvel.");
  }
  if (property.slug !== imovel.value.slug) {
    throw new Error("A API devolveu o estado de outro imóvel. Actualize a página e tente novamente.");
  }

  imovel.value.id = property.id;
  imovel.value.status = property.status;
  imovel.value.rejection_reason = property.rejection_reason || "";
  managedPropertyLoaded.value = true;
  return property;
};

const handleActivate = async () => {
  activationError.value = null;
  actionLoading.value = imovel.value.slug;
  try {
    const property = await getManagedProperty();
    const result = await store.dispatch(
      "subscriptions/activateProperty",
      property.id,
    );
    imovel.value.status = result.property?.status || "pending_review";
    try {
      await store.dispatch("subscriptions/getMySubscription");
    } catch (error) {
      console.error("Error refreshing subscription:", error);
    }
    Swal.fire({
      title: "Enviado!",
      text: `"${imovel.value.title}" aguarda validação de um administrador.`,
      icon: "success",
      timer: 2400,
      showConfirmButton: false,
    });
  } catch (error) {
    const data = error.response?.data;
    const reason = data?.reason;
    if (reason === "sem_subscricao") {
      activationError.value = "Não tem subscrição activa. Contacte o suporte.";
    } else if (reason === "quota_esgotada") {
      activationError.value = `Quota esgotada: ${data.usados} de ${data.quota} slots usados no plano "${data.plano}". Faça upgrade para continuar.`;
    } else if (reason === "fotos_excedidas") {
      activationError.value = `O seu pacote permite até ${data.max_fotos} fotografias. Remova ${data.fotos - data.max_fotos} foto(s) antes de enviar.`;
    } else {
      activationError.value =
        data?.message ||
        error.message ||
        "Erro ao enviar o imóvel para validação. Tente novamente.";
    }
    Swal.fire({ title: "Erro", text: activationError.value, icon: "error" });
  } finally {
    actionLoading.value = null;
  }
};

const approveProperty = async () => {
  const confirmation = await Swal.fire({
    title: "Activar imóvel?",
    text: `"${imovel.value.title}" será aprovado e publicado.`,
    icon: "question",
    showCancelButton: true,
    confirmButtonText: "Aprovar e activar",
    cancelButtonText: "Cancelar",
  });
  if (!confirmation.isConfirmed) return;

  activationError.value = null;
  actionLoading.value = imovel.value.slug;
  try {
    const property = await getManagedProperty();
    const { data } = await apiClient.post(
      `/moderation/property/${property.id}/approve`,
    );
    imovel.value.status = data.data?.status || "active";
    try {
      await store.dispatch("subscriptions/getMySubscription");
    } catch (refreshError) {
      console.error("Error refreshing subscription after approval:", refreshError);
    }
    Swal.fire({
      title: "Imóvel activado",
      text: `"${imovel.value.title}" foi aprovado e publicado.`,
      icon: "success",
      timer: 2200,
      showConfirmButton: false,
    });
  } catch (error) {
    activationError.value =
      error.response?.data?.message ||
      error.message ||
      "Não foi possível activar o imóvel.";
    Swal.fire({ title: "Erro", text: activationError.value, icon: "error" });
  } finally {
    actionLoading.value = null;
  }
};

const confirmDeactivate = async () => {
  const result = await Swal.fire({
    title: "Desactivar imóvel?",
    text: `"${imovel.value.title}" ficará oculto na plataforma.`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Sim, desactivar",
    cancelButtonText: "Cancelar",
  });
  if (!result.isConfirmed) return;

  actionLoading.value = imovel.value.slug;
  try {
    const property = await getManagedProperty();
    const response = await store.dispatch(
      "subscriptions/deactivateProperty",
      property.id,
    );
    imovel.value.status = response.property?.status || "inactive";
    try {
      await store.dispatch("subscriptions/getMySubscription");
    } catch (error) {
      console.error("Error refreshing subscription:", error);
    }
    Swal.fire({
      title: "Desactivado!",
      text: `"${imovel.value.title}" foi desactivado.`,
      icon: "success",
      timer: 2000,
      showConfirmButton: false,
    });
  } catch (error) {
    Swal.fire({
      title: "Erro",
      text:
        error.response?.data?.message ||
        error.message ||
        "Não foi possível desactivar o imóvel. Tente novamente.",
      icon: "error",
    });
  } finally {
    actionLoading.value = null;
  }
};
</script>

<style scoped>
/* ── Page ──────────────────────────────────────────── */
.details-page {
  background: #f4f6fb;
  min-height: 100vh;
}
.details-loading {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f4f6fb;
}
.inner {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

/* ── Gallery section ───────────────────────────────── */
.gallery-section {
  background: #fff;
  padding: 24px 0 32px;
  border-bottom: 1px solid #e8ecf4;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  border: none;
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  margin-bottom: 20px;
  transition: background 0.18s, color 0.18s;
}
.back-btn:hover {
  background: #0d1b2a;
  color: #fff;
}

/* Main image */
.main-img-wrap {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(13, 27, 42, 0.1);
}
.main-img {
  display: block;
}

/* Badges */
.img-badges {
  position: absolute;
  top: 14px;
  left: 14px;
  display: flex;
  gap: 8px;
  z-index: 2;
}
.img-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.badge-green {
  background: rgba(22, 163, 74, 0.92);
  color: #fff;
}
.badge-blue {
  background: rgba(37, 99, 235, 0.92);
  color: #fff;
}
.badge-dark {
  background: rgba(13, 27, 42, 0.78);
  color: #d4af37;
}

/* Nav arrows */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0d1b2a;
  transition: background 0.18s, box-shadow 0.18s;
  z-index: 2;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
}
.nav-btn:hover {
  background: #fff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
}
.nav-left {
  left: 14px;
}
.nav-right {
  right: 14px;
}

/* Photo counter */
.photo-count {
  position: absolute;
  bottom: 14px;
  right: 14px;
  background: rgba(13, 27, 42, 0.72);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 5px;
  z-index: 2;
}

/* Thumbnails */
.thumb-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  height: 100%;
}
.thumb-item {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid transparent;
  transition: border-color 0.18s, box-shadow 0.18s;
}
.thumb-item:hover {
  box-shadow: 0 4px 14px rgba(13, 27, 42, 0.14);
}
.thumb-active {
  border-color: #d4af37 !important;
}
.thumb-more {
  position: absolute;
  inset: 0;
  background: rgba(13, 27, 42, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 22px;
  font-weight: 800;
}

/* ── Info section ──────────────────────────────────── */
.info-section {
  padding: 36px 0 72px;
}

/* Title */
.title-block {
  margin-bottom: 24px;
}
.prop-type-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: #d4af37;
  margin-bottom: 8px;
}
.prop-title {
  font-size: 26px;
  font-weight: 800;
  color: #0d1b2a;
  margin: 0 0 10px;
  line-height: 1.25;
}
.location-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 20px;
}
.location-row .q-icon {
  color: #94a3b8;
  flex-shrink: 0;
}

.price-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94a3b8;
  margin-bottom: 4px;
}
.price-value {
  font-size: 32px;
  font-weight: 800;
  color: #0d1b2a;
  letter-spacing: -1px;
  line-height: 1;
}

.section-div {
  height: 1px;
  background: #e8ecf4;
  margin: 28px 0;
}

.info-block {
  margin-bottom: 8px;
}
.block-title {
  font-size: 14px;
  font-weight: 700;
  color: #0d1b2a;
  margin-bottom: 18px;
}

/* Feature cards */
.feat-card {
  background: #fff;
  border: 1px solid #e8ecf4;
  border-radius: 14px;
  padding: 18px 12px;
  text-align: center;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.feat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(13, 27, 42, 0.08);
}
.feat-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
  color: #0d1b2a;
}
.feat-value {
  font-size: 22px;
  font-weight: 800;
  color: #0d1b2a;
  line-height: 1;
  margin-bottom: 4px;
}
.feat-label {
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Description */
.desc-text {
  font-size: 14px;
  line-height: 1.9;
  color: #475569;
  margin: 0;
}

/* ── Contact card ──────────────────────────────────── */
.contact-card {
  background: #fff;
  border: 1px solid #e8ecf4;
  border-radius: 18px;
  padding: 24px;
  position: sticky;
  top: 24px;
  box-shadow: 0 4px 20px rgba(13, 27, 42, 0.06);
}

.agent-row {
  display: flex;
  align-items: center;
  gap: 14px;
}
.agent-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid #e8ecf4;
}
.agent-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.agent-name {
  font-size: 15px;
  font-weight: 700;
  color: #0d1b2a;
}
.agent-role {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}

.card-div {
  height: 1px;
  background: #f1f5f9;
  margin: 18px 0;
}

.contact-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.cta-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  border: none;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.18s, transform 0.18s;
  letter-spacing: 0.2px;
}
.cta-btn:hover {
  opacity: 0.88;
  transform: translateY(-1px);
}
.btn-wa {
  background: #16a34a;
  color: #fff;
}
.btn-call {
  background: #0d1b2a;
  color: #d4af37;
}
.btn-delete {
  background: rgba(220, 38, 38, 0.06);
  color: #dc2626;
  border: 1px solid rgba(220, 38, 38, 0.2);
}
.btn-delete:hover:not(:disabled) {
  background: rgba(220, 38, 38, 0.12);
  opacity: 1;
  transform: none;
}
.btn-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-edit {
  color: #d4af37;
  background: #0d1b2a;
  border: 1px solid rgba(220, 38, 38, 0.2);
}
.btn-edit:hover:not(:disabled) {
  background: #d4af37;
  color: #0d1b2a;
  opacity: 1;
  transform: none;
}
.btn-edit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
/* Meta rows */
.meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 13px;
  border-bottom: 1px solid #f8fafc;
}
.meta-row:last-child {
  border-bottom: none;
}
.meta-key {
  color: #94a3b8;
  font-weight: 500;
}
.meta-val {
  color: #0d1b2a;
  font-weight: 600;
}

/* ── Gallery dialog ────────────────────────────────── */
.gallery-carousel {
  width: 90vw;
  max-width: 1200px;
  height: 80vh;
}
:deep(.gallery-carousel .q-carousel__slide) {
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
}

/* ── Estado / activar / desactivar ─────────────────── */
.status-block .meta-row { border-bottom: none; padding-bottom: 12px; }

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 10px;
  padding: 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.2px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s;
}
.action-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.action-btn--activate {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
  border-color: rgba(34, 197, 94, 0.25);
}
.action-btn--activate:not(:disabled):hover { background: rgba(34, 197, 94, 0.18); }

.action-btn--deactivate {
  background: rgba(239, 68, 68, 0.08);
  color: #dc2626;
  border-color: rgba(239, 68, 68, 0.2);
}
.action-btn--deactivate:not(:disabled):hover { background: rgba(239, 68, 68, 0.15); }

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
}
.status-badge--active         { background: rgba(34,197,94,.12);  color: #16a34a; }
.status-badge--expired        { background: rgba(239,68,68,.1);   color: #dc2626; }
.status-badge--pending_review { background: rgba(59,130,246,.1);  color: #2563eb; }
.status-badge--inactive,
.status-badge--draft          { background: rgba(148,163,184,.12); color: #94a3b8; }

.pending-note,
.reject-note {
  font-size: 12px;
  margin-top: 10px;
  text-align: center;
}
.pending-note { color: #64748b; }
.reject-note  { color: #dc2626; text-align: left; margin-bottom: 10px; }

@media (max-width: 1023px) {
  .contact-card {
    position: relative;
    top: 0;
  }
}
</style>
